import React, { useCallback, useState, createElement } from 'react';
import { Holding, LiquidityEvent } from '../types';
import { HoldingsForm } from '../components/HoldingsForm';
import { HoldingsList } from '../components/HoldingsList';
import { LiquidityEventForm } from '../components/LiquidityEventForm';
import { LiquidityEventList } from '../components/LiquidityEventList';
import { LoanForm } from '../components/LoanForm';
import { LoanList } from '../components/LoanList';
import { RealEstateForm } from '../components/RealEstateForm';
import { Wallet, CalendarClock, Landmark, Building2, Plus, X, Download } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Currency } from '../types';
function escapeCsvField(value: string | number | undefined | null): string {
  const str = String(value ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}
function downloadCsv(filename: string, headers: string[], rows: (string | number | undefined | null)[][]) {
  const csv = [headers.map(escapeCsvField).join(','), ...rows.map(row => row.map(escapeCsvField).join(','))].join('\n');
  const blob = new Blob([csv], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
interface HoldingsPageProps {
  holdings: Holding[];
  liquidityEvents: LiquidityEvent[];
  loans?: any[];
  partnerNames: [string, string];
  baseCurrency?: Currency;
  baseCountry?: string;
  onAddHolding: (holding: Omit<Holding, 'id' | 'createdAt' | 'history'>) => void;
  onUpdateHoldingAmount: (id: string, newAmount: number) => void;
  onRenameHolding: (id: string, newName: string) => void;
  onUpdateCountry: (id: string, newCountry: string) => void;
  onUpdateHoldingFull?: (id: string, updates: Partial<Omit<Holding, 'id' | 'createdAt' | 'history'>>) => void;
  onToggleHoldingActive?: (id: string) => void;
  onDeleteHolding: (id: string) => void;
  onAddEvent: (event: Omit<LiquidityEvent, 'id'>) => string | void;
  onDeleteEvent: (id: string) => void;
  onUpdateEvent?: (id: string, updates: Partial<Omit<LiquidityEvent, 'id'>>) => void;
  onToggleEventActive?: (id: string) => void;
  onAddLoan?: (loan: any) => string | void;
  onDeleteLoan?: (id: string) => void;
  onUpdateLoan?: (id: string, updates: Partial<Omit<any, 'id' | 'createdAt'>>) => void;
  onToggleLoanActive?: (id: string) => void;
}
export function HoldingsPage({
  holdings,
  liquidityEvents,
  loans = [],
  partnerNames,
  baseCurrency = 'USD',
  baseCountry = 'US',
  onAddHolding,
  onUpdateHoldingAmount,
  onRenameHolding,
  onUpdateCountry,
  onUpdateHoldingFull = () => {},
  onToggleHoldingActive = () => {},
  onDeleteHolding,
  onAddEvent,
  onDeleteEvent,
  onUpdateEvent = () => {},
  onToggleEventActive = () => {},
  onAddLoan = () => {},
  onDeleteLoan = () => {},
  onUpdateLoan = () => {},
  onToggleLoanActive = () => {}
}: HoldingsPageProps) {
  const [activeTab, setActiveTab] = useState<'holdings' | 'realestate' | 'events' | 'loans'>('holdings');
  const [showForm, setShowForm] = useState(false);
  const [editingHoldingId, setEditingHoldingId] = useState<string | null>(null);
  const exportHoldings = useCallback(() => {
    const headers = ['Person', 'Name', 'Amount', 'Currency', 'Type', 'Country', 'Created', 'Is Real Estate', 'Purchase Price', 'Appreciation Rate %', 'Closing Costs', 'Rental Income (Monthly)', 'Linked Loan ID'];
    const rows = holdings.map(h => [h.person, h.name, h.amount, h.currency, h.isRealEstate ? 'Real Estate' : h.type, h.country, h.createdAt ? new Date(h.createdAt).toLocaleDateString() : '', h.isRealEstate ? 'Yes' : 'No', h.purchasePrice ?? '', h.appreciationRate ?? '', h.closingCosts ?? '', h.rentalIncome ?? '', h.linkedLoanId ?? '']);
    downloadCsv(`pairfolio-holdings-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  }, [holdings]);
  const exportEvents = useCallback(() => {
    const headers = ['Name', 'Amount', 'Currency', 'Frequency', 'Start Year', 'End Year'];
    const rows = liquidityEvents.map(e => [e.name, e.amount, e.currency, e.type, e.startYear, e.endYear ?? 'Perpetual']);
    downloadCsv(`pairfolio-cash-events-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  }, [liquidityEvents]);
  const exportLoans = useCallback(() => {
    const headers = ['Name', 'Owner', 'Total Amount', 'Deposit', 'Currency', 'Interest Rate', 'Duration (months)', 'Repayment Type', 'Start Year', 'Active'];
    const rows = loans.map((l: any) => [l.name, l.person, l.totalAmount, l.depositAmount, l.currency, `${l.interestRate}%`, l.durationMonths, l.repaymentType, l.startYear, l.isActive ? 'Yes' : 'No']);
    downloadCsv(`pairfolio-loans-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  }, [loans]);
  const tabConfig = {
    holdings: {
      title: 'Holdings',
      subtitle: `${holdings.filter(h => !h.isRealEstate).length} asset${holdings.filter(h => !h.isRealEstate).length !== 1 ? 's' : ''} tracked`,
      addLabel: 'Add Holding',
      icon: Wallet,
      hasData: holdings.length > 0
    },
    realestate: {
      title: 'Real Estate',
      subtitle: `${holdings.filter(h => h.isRealEstate).length} propert${holdings.filter(h => h.isRealEstate).length !== 1 ? 'ies' : 'y'} tracked`,
      addLabel: 'Add Property',
      icon: Building2,
      hasData: holdings.filter(h => h.isRealEstate).length > 0
    },
    events: {
      title: 'Cash Events',
      subtitle: `${liquidityEvents.filter(e => e.isActive ?? true).length} active event${liquidityEvents.filter(e => e.isActive ?? true).length !== 1 ? 's' : ''} of ${liquidityEvents.length}`,
      addLabel: 'Add Event',
      icon: CalendarClock,
      hasData: liquidityEvents.length > 0
    },
    loans: {
      title: 'Loans & Debts',
      subtitle: `${loans.filter((l: any) => l.isActive).length} active loan${loans.filter((l: any) => l.isActive).length !== 1 ? 's' : ''}`,
      addLabel: 'Add Loan',
      icon: Landmark,
      hasData: loans.length > 0
    }
  };
  const current = tabConfig[activeTab];
  // Auto-close form when switching tabs
  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab);
    setShowForm(false);
  };
  // Wrap onAdd handlers to close form after successful add
  const handleAddHolding = (holding: Omit<Holding, 'id' | 'createdAt' | 'history'>) => {
    onAddHolding(holding);
    setShowForm(false);
  };
  const handleAddEvent = (event: Omit<LiquidityEvent, 'id'>) => {
    onAddEvent(event);
    setShowForm(false);
  };
  const handleAddLoan = (loan: any) => {
    onAddLoan(loan);
    setShowForm(false);
  };
  const handleEditHolding = (id: string, updates: Partial<Omit<Holding, 'id' | 'createdAt' | 'history'>>) => {
    onUpdateHoldingFull(id, updates);
    setEditingHoldingId(null);
  };
  const editingHolding = editingHoldingId ? holdings.find(h => h.id === editingHoldingId) : undefined;
  return <div className="flex flex-col md:flex-row gap-4 md:gap-6 min-h-[calc(100vh-8rem)]" data-id="element-1381">
      {/* Horizontal tabs on mobile, vertical sidebar on desktop */}
      <div className="md:w-48 md:shrink-0" data-id="element-1382">
        <div className="flex md:flex-col md:sticky md:top-20 gap-1 overflow-x-auto pb-2 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide" data-id="element-1383">
          {(['holdings', 'realestate', 'events', 'loans'] as const).map(tab => {
          const config = tabConfig[tab];
          const Icon = config.icon;
          return <button key={tab} onClick={() => handleTabChange(tab)} className={`flex items-center gap-2 whitespace-nowrap py-2 px-3 text-sm font-medium rounded-lg transition-all text-left ${activeTab === tab ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'}`} data-id="element-1384">
                  <Icon className="w-4 h-4 shrink-0" data-id="element-1385" />
                  {config.title}
                </button>;
        })}
        </div>
      </div>

      {/* Content Area — full width */}
      <div className="flex-1 min-w-0 space-y-5" data-id="element-1386">
        {/* Content Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-1387">
          <div data-id="element-1388">
            <h2 className="text-lg font-semibold text-slate-900" data-id="element-1389">
              {current.title}
            </h2>
            <p className="text-sm text-slate-500" data-id="element-1390">{current.subtitle}</p>
          </div>
          <div className="flex items-center gap-2" data-id="element-1391">
            {current.hasData && <Button variant="secondary" size="md" onClick={() => {
            if (activeTab === 'holdings' || activeTab === 'realestate') exportHoldings();else if (activeTab === 'events') exportEvents();else if (activeTab === 'loans') exportLoans();
          }} data-id="element-1392">
                <Download className="w-4 h-4 mr-1.5" data-id="element-1393" />
                Export
              </Button>}
            <Button variant={showForm ? 'secondary' : 'primary'} size="md" onClick={() => setShowForm(!showForm)} data-id="element-1394">
              {showForm ? <>
                  <X className="w-4 h-4 mr-1.5" data-id="element-1395" />
                  Cancel
                </> : <>
                  <Plus className="w-4 h-4 mr-1.5" data-id="element-1396" />
                  {current.addLabel}
                </>}
            </Button>
          </div>
        </div>

        {/* Collapsible Form Panel */}
        {showForm && <div className="animate-in slide-in-from-top-2 fade-in duration-200" data-id="element-1397">
            {activeTab === 'holdings' && <HoldingsForm onAdd={handleAddHolding} partnerNames={partnerNames} baseCountry={baseCountry} baseCurrency={baseCurrency} data-id="element-1398" />}
            {activeTab === 'realestate' && <RealEstateForm onAdd={handleAddHolding} partnerNames={partnerNames} loans={loans} liquidityEvents={liquidityEvents} holdings={holdings} onAddLoan={onAddLoan} onAddEvent={onAddEvent} baseCurrency={baseCurrency} baseCountry={baseCountry} data-id="element-1399" />}
            {activeTab === 'events' && <LiquidityEventForm onAdd={handleAddEvent} baseCurrency={baseCurrency} data-id="element-1400" />}
            {activeTab === 'loans' && <LoanForm onAdd={handleAddLoan} partnerNames={partnerNames} holdings={holdings} baseCurrency={baseCurrency} data-id="element-1401" />}
          </div>}

        {/* Full-width List */}
        <div className="animate-in fade-in duration-300" data-id="element-1402">
          {(activeTab === 'holdings' || activeTab === 'realestate') && <HoldingsList holdings={activeTab === 'realestate' ? holdings.filter(h => h.isRealEstate) : holdings.filter(h => !h.isRealEstate)} onDelete={onDeleteHolding} onUpdateAmount={onUpdateHoldingAmount} onRename={onRenameHolding} onUpdateCountry={onUpdateCountry} partnerNames={partnerNames} onExport={exportHoldings} onEditHolding={id => setEditingHoldingId(id)} onToggleActive={onToggleHoldingActive} data-id="element-1403" />}
          {activeTab === 'events' && <LiquidityEventList events={liquidityEvents} onDelete={onDeleteEvent} onUpdate={onUpdateEvent} onToggleActive={onToggleEventActive} onExport={exportEvents} data-id="element-1404" />}
          {activeTab === 'loans' && <LoanList loans={loans} partnerNames={partnerNames} onDelete={onDeleteLoan} onUpdate={onUpdateLoan} onToggleActive={onToggleLoanActive} onExport={exportLoans} data-id="element-1405" />}
        </div>
      </div>

      {/* Edit Real Estate Modal */}
      {editingHolding && editingHolding.isRealEstate && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm overflow-y-auto" data-id="element-1406">
          <div className="relative w-full max-w-2xl max-h-full my-8" data-id="element-1407">
            <RealEstateForm onAdd={handleAddHolding} partnerNames={partnerNames} loans={loans} liquidityEvents={liquidityEvents} holdings={holdings} onAddLoan={onAddLoan} onAddEvent={onAddEvent} baseCurrency={baseCurrency} baseCountry={baseCountry} editingHolding={editingHolding} onEdit={handleEditHolding} onCancelEdit={() => setEditingHoldingId(null)} data-id="element-1408" />
          </div>
        </div>}

      {/* Edit Non-RE Holding Modal */}
      {editingHolding && !editingHolding.isRealEstate && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm overflow-y-auto" data-id="element-1409">
          <div className="relative w-full max-w-2xl max-h-full my-8" data-id="element-1410">
            <HoldingsForm onAdd={handleAddHolding} partnerNames={partnerNames} baseCountry={baseCountry} baseCurrency={baseCurrency} editingHolding={editingHolding} onEdit={handleEditHolding} onCancelEdit={() => setEditingHoldingId(null)} data-id="element-1411" />
          </div>
        </div>}
    </div>;
}